import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useToast } from '../../context/ToastContext';
import { CashForecastService } from '../../services/cashForecast.service';
import {
  AdjustmentRecurrence, FORECAST_CONFIDENCES, ForecastAdjustment, ForecastAdjustmentRequest, ForecastConfidence,
  ForecastDirection,
} from '../../interfaces/cashForecast.interface';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';

interface Props { institutionId: string; canManage: boolean; onClose: () => void; onChanged: () => void }

const FIELD = { flex: '1 1 180px', maxWidth: 260 } as const;

const blank = (): ForecastAdjustmentRequest => ({
  adjustmentDate: toLocalIsoDate(), direction: 'OUTFLOW', amount: 0, category: '', description: '',
  confidence: 'EXPECTED', recurrence: 'NONE', recurUntil: '',
});

/** Manual forecast lines (rent, a capex payment, a loan received). Data only: never posted to the ledger. */
const ForecastAdjustmentsDialog: React.FC<Props> = ({ institutionId, canManage, onClose, onChanged }) => {
  const toast = useToast();
  const [rows, setRows] = useState<ForecastAdjustment[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ForecastAdjustmentRequest>(blank());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await CashForecastService.listAdjustments(institutionId));
    } catch (err) {
      toast.showApiError(err, 'Could not load the manual lines');
      setRows([]);
    }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  const edit = (a: ForecastAdjustment) => {
    setEditingId(a.id);
    setForm({
      adjustmentDate: a.adjustmentDate, direction: a.direction, amount: a.amount, category: a.category,
      description: a.description ?? '', confidence: a.confidence, recurrence: a.recurrence, recurUntil: a.recurUntil ?? '',
    });
  };

  const reset = () => { setEditingId(null); setForm(blank()); };

  const valid = form.category.trim() !== '' && Number(form.amount) > 0 && form.adjustmentDate !== ''
    && (form.recurrence === 'NONE' || (form.recurUntil ?? '') >= form.adjustmentDate && (form.recurUntil ?? '') !== '');

  const save = async () => {
    setSaving(true);
    const body: ForecastAdjustmentRequest = {
      ...form, amount: Number(form.amount), description: form.description?.trim() || undefined,
      recurUntil: form.recurrence === 'NONE' ? undefined : form.recurUntil || undefined,
    };
    try {
      if (editingId) await CashForecastService.updateAdjustment(institutionId, editingId, body);
      else await CashForecastService.createAdjustment(institutionId, body);
      toast.showSuccess('Manual line saved');
      reset();
      await load();
      onChanged();
    } catch (err) {
      toast.showApiError(err, 'Could not save the manual line');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: ForecastAdjustment) => {
    try {
      await CashForecastService.deleteAdjustment(institutionId, a.id);
      toast.showSuccess('Manual line removed');
      if (editingId === a.id) reset();
      await load();
      onChanged();
    } catch (err) {
      toast.showApiError(err, 'Could not remove the manual line');
    }
  };

  return (
    <Dialog data-help-page="cashForecast.adjust.page" open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manual forecast lines</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For cash you know about that no document shows yet: rent, a capex payment, a loan to be received. These lines
          only feed the forecast; they never reach the ledger.
        </Typography>
        {rows === null ? <CircularProgress size={24} /> : (
          <Table data-help-section="banking.cashForecast.manual" size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.cashForecast.manual.col.date">First date</TableCell><TableCell data-help="banking.cashForecast.manual.col.direction">Direction</TableCell><TableCell data-help="banking.cashForecast.manual.col.category">Category</TableCell>
                <TableCell data-help="banking.cashForecast.manual.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.cashForecast.manual.col.repeats">Repeats</TableCell><TableCell data-help="banking.cashForecast.manual.col.confidence">Confidence</TableCell>
                {canManage && <TableCell data-help="banking.cashForecast.manual.col.actions" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={canManage ? 7 : 6}><Typography color="text.secondary">No manual lines yet.</Typography></TableCell></TableRow>
              )}
              {rows.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{formatDate(a.adjustmentDate)}</TableCell>
                  <TableCell><Chip size="small" color={a.direction === 'INFLOW' ? 'success' : 'default'} label={humanizeEnum(a.direction)} /></TableCell>
                  <TableCell>{a.category}{a.description ? ` - ${a.description}` : ''}</TableCell>
                  <TableCell align="right">{formatMoney(a.amount)}</TableCell>
                  <TableCell>{a.recurrence === 'NONE' ? 'Once' : `${humanizeEnum(a.recurrence)} until ${a.recurUntil ? formatDate(a.recurUntil) : '-'}`}</TableCell>
                  <TableCell>{humanizeEnum(a.confidence)}</TableCell>
                  {canManage && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton data-help="banking.cashForecast.manual.action.edit" size="small" aria-label={`Edit ${a.category}`} onClick={() => edit(a)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton data-help="banking.cashForecast.manual.action.remove" size="small" aria-label={`Remove ${a.category}`} onClick={() => remove(a)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {canManage && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{editingId ? 'Edit line' : 'Add a line'}</Typography>
            <Box data-help-section="cashForecast.adjust.section.form" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField data-help="cashForecast.adjust.field.firstDate" sx={FIELD} size="small" type="date" label="First date" InputLabelProps={{ shrink: true }}
                value={form.adjustmentDate} onChange={e => setForm({ ...form, adjustmentDate: e.target.value })} />
              <TextField data-help="cashForecast.adjust.field.direction" sx={FIELD} size="small" select label="Direction" value={form.direction}
                onChange={e => setForm({ ...form, direction: e.target.value as ForecastDirection })}>
                <MenuItem value="OUTFLOW">Outflow (paid out)</MenuItem>
                <MenuItem value="INFLOW">Inflow (received)</MenuItem>
              </TextField>
              <TextField data-help="cashForecast.adjust.field.amount" sx={FIELD} size="small" type="number" label="Amount (BDT)" value={form.amount || ''} InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, step: '0.01' }} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} />
              <TextField data-help="cashForecast.adjust.field.category" sx={FIELD} size="small" label="Category" value={form.category} InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 80 }} onChange={e => setForm({ ...form, category: e.target.value })} />
              <TextField data-help="cashForecast.adjust.field.description" sx={{ flex: '2 1 280px', maxWidth: 520 }} size="small" label="Description (optional)" value={form.description ?? ''} InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 500 }} onChange={e => setForm({ ...form, description: e.target.value })} />
              <TextField data-help="cashForecast.adjust.field.confidence" sx={FIELD} size="small" select label="Confidence" value={form.confidence}
                onChange={e => setForm({ ...form, confidence: e.target.value as ForecastConfidence })}>
                {FORECAST_CONFIDENCES.map(c => <MenuItem key={c} value={c}>{humanizeEnum(c)}</MenuItem>)}
              </TextField>
              <TextField data-help="cashForecast.adjust.field.repeats" sx={FIELD} size="small" select label="Repeats" value={form.recurrence}
                onChange={e => setForm({ ...form, recurrence: e.target.value as AdjustmentRecurrence })}>
                <MenuItem value="NONE">Once</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </TextField>
              {form.recurrence !== 'NONE' && (
                <TextField data-help="cashForecast.adjust.field.repeatUntil" sx={FIELD} size="small" type="date" label="Repeat until" InputLabelProps={{ shrink: true }}
                  value={form.recurUntil ?? ''} onChange={e => setForm({ ...form, recurUntil: e.target.value })} />
              )}
            </Box>
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <Button data-help="banking.cashForecast.manual.save" variant="contained" onClick={save} disabled={!valid || saving}>{editingId ? 'Update line' : 'Add line'}</Button>
              {editingId && <Button data-help="cashForecast.adjust.cancelEdit" onClick={reset}>Cancel edit</Button>}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions><Button data-help="cashForecast.adjust.close" onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default ForecastAdjustmentsDialog;
